<template>

  <div class="nav-select" v-if="!accommodation.reserve_whole_object">
    <div class="nav-item" v-for="unit in accommodation.units.filter(u => u.hidden_for_reservation === false)">
      <div class="btn btn-first fw-bolder" v-if="selectedUnitTab === unit.id" aria-current="page"
           @click="changeSelectedTab(unit)">{{ unit.title }}
      </div>
      <div class="btn btn-outline-first" v-else aria-current="page"
           @click="changeSelectedTab(unit)">{{ unit.title }}
      </div>
    </div>
  </div>

  <PartsTempPreloader v-if="loading"/>
  <LazyDateRangePicker
      v-if="!loading"
      :minDate="(new Date()).setHours(1)"
      :disabled_dates="reservedDates"
      :prevent_select="false"
      :show-legend="true"
      ref="calendarRef"
      @onSelect="prepareReservation"
  />

</template>

<script setup lang="ts">
import dayjs from "dayjs";
import {onMounted, PropType, ref, shallowRef, watch} from "vue";
import {useNuxtApp, useRoute} from "#app";
import Accommodation from "../../../../../lib/models/Accommodation";
import LazyDateRangePicker from "~/components/web/parts/calendar/calendar_range_picker_double";
import useStateAccommodationList from "../../../../../composables/state/accommodation_list";
import {formatTime} from "../../../../../lib/functions";
import {useStateClientData} from "../../../../../composables/state/client_state";

const props = defineProps({
  accommodation: Object as PropType<Accommodation>
});

const emit = defineEmits(['clickAction']);

const reservedDates = ref([]);
const selectedUnit = ref(null);
const selectedUnitTab = ref(null);
const calendarRef = ref(null);
const loading = ref(false);

const {$tyrionApi} = useNuxtApp();
const {filterData} = useStateAccommodationList();
const {clientTripData, setClientTripData} = useStateClientData();

onMounted(() => {
  if (props.accommodation.units && props.accommodation.units.length > 0) {
    selectedUnit.value = props.accommodation.units[0];
    selectedUnitTab.value = selectedUnit.value.id;
    loadAvailableDays();
  }else{
    selectFilteredDate();
  }

});

watch(() => clientTripData.value, (newVal) => {
  if (newVal.from && newVal.to) {
    calendarRef.value.setSelectedDates({
      'arrivalDate': dayjs(newVal.from).add(12, 'hours').toDate(),
      'leaveDate': dayjs(newVal.to).add(6, 'hours').toDate()
    });
  }
})


const selectFilteredDate = () => {
  const storeFilterData = filterData.value;
  const queryData = useRoute().query;

  let arrivalDate = null;
  let leaveDate = null;
  if (queryData && queryData.date_from) {
    arrivalDate = queryData.date_from;
  } else if (storeFilterData && storeFilterData.date && storeFilterData.date.startDate && queryData.clear_date != 1) {
    arrivalDate = formatTime(storeFilterData.date.startDate, 'YYYY-MM-DD');
  }

  if (queryData && queryData.date_to) {
    leaveDate = queryData.date_to;
  } else if (storeFilterData && storeFilterData.date && storeFilterData.date.endDate && queryData.clear_date != 1) {
    leaveDate = formatTime(storeFilterData.date.endDate, 'YYYY-MM-DD');
  }

  if (calendarRef.value && arrivalDate && leaveDate) {
    calendarRef.value.setSelectedDates({'arrivalDate': dayjs(arrivalDate).add(12, 'hours').toDate(), 'leaveDate': dayjs(leaveDate).add(6, 'hours').toDate()});
  }
}

const prepareReservation = (dateRange) => {
  setClientTripData({from: (new Date(formatTime(dateRange.startDate, 'YYYY-MM-DD'))), to: (new Date(formatTime(dateRange.endDate, 'YYYY-MM-DD')))});
}

const changeSelectedTab = (unit) => {
  selectedUnitTab.value = unit.id;
  selectedUnit.value = unit;
  loadAvailableDays();
}

const loadAvailableDays = () => {

  if (selectedUnit.value && selectedUnit.value.id > 0) {
    reservedDates.value = [];
    loading.value = true;
    $tyrionApi.postTyrionUrl('/api3/accommodation/reserved-days', {
      body: {
        id_accommodation: props.accommodation.id,
        id_unit: [selectedUnit.value.id]
      }, cacheTime: (60 * 5)
    }).then(res => {
      res.data.forEach(reservation => {
        if(reservation.dates.length > 0) {
          const datesArray = [
            dayjs(reservation.DateFrom),
            ...reservation.dates.map(date => dayjs(date)),
            dayjs(reservation.DateTo)
          ];
          reservedDates.value.push(datesArray);
        }else{
          reservedDates.value.push([dayjs(reservation.DateFrom), dayjs(reservation.DateTo)]);
        }
      });
      loading.value = false;
    }).catch(e => {
      loading.value = false;
      console.log((e.response && e.response.data && e.response.data.message) ? e.response.data.message : `Reservation error: ${e.message}`);
    }).finally(() => {
      selectFilteredDate();
    })

  }
};

</script>
